import { useCallback, useEffect, useRef, useState } from 'react';

import {
    Check,
    ContentCopy,
    Refresh,
    ThumbDown,
    ThumbDownOutlined,
    ThumbUp,
    ThumbUpOutlined,
} from '@mui/icons-material';
import { IconButton, Stack, Tooltip, Typography } from '@mui/material';

import { themeColors } from 'colors';
import useSubmitMessageFeedback from 'hooks/mutations/useSubmitMessageFeedback';

import DislikeFeedbackPanel from '../DislikeFeedbackPanel';
import SaraCircleMark from '../SaraCircleMark';
import { ChatMessageFeedback, FeedbackTag, MessageFeedbackRating } from '../utils';

const ASSISTANT_TEXT_COLOR = themeColors.primary.main;
const COPIED_RESET_DELAY_MS = 2000;

export type Props = {
    content: string;
    isLoading?: boolean;
    onRetry?: () => void;
    /** Present only for real assistant messages; absent for the static greeting. */
    messageId?: number;
    initialFeedback?: ChatMessageFeedback | null;
};

const AssistantMessageBubble = ({ content, isLoading = false, onRetry, messageId, initialFeedback }: Props) => {
    const [copied, setCopied] = useState(false);
    const copiedResetTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
    const [currentRating, setCurrentRating] = useState<MessageFeedbackRating | null>(initialFeedback?.rating ?? null);
    const [showDislikePanel, setShowDislikePanel] = useState(false);

    // Called unconditionally per Rules of Hooks; only invoked when messageId is defined.
    const { mutate: submitFeedback } = useSubmitMessageFeedback(messageId ?? 0);

    useEffect(
        () => () => {
            if (copiedResetTimeoutRef.current) {
                clearTimeout(copiedResetTimeoutRef.current);
                copiedResetTimeoutRef.current = null;
            }
        },
        []
    );

    const handleCopy = useCallback(() => {
        navigator.clipboard
            .writeText(content)
            .then(() => {
                if (copiedResetTimeoutRef.current) {
                    clearTimeout(copiedResetTimeoutRef.current);
                    copiedResetTimeoutRef.current = null;
                }
                setCopied(true);
                copiedResetTimeoutRef.current = setTimeout(() => {
                    copiedResetTimeoutRef.current = null;
                    setCopied(false);
                }, COPIED_RESET_DELAY_MS);
            })
            .catch(() => {});
    }, [content]);

    const handleLike = useCallback(() => {
        setShowDislikePanel(false);
        setCurrentRating('like');
        submitFeedback({ rating: 'like' });
    }, [submitFeedback]);

    const handleDislike = useCallback(() => {
        if (currentRating === 'dislike' && showDislikePanel) {
            setShowDislikePanel(false);
            setCurrentRating(null);
            return;
        }
        setCurrentRating('dislike');
        setShowDislikePanel(true);
    }, [currentRating, showDislikePanel]);

    const handleDislikeSubmit = useCallback(
        (tags: FeedbackTag[], reviewText: string) => {
            submitFeedback({ rating: 'dislike', tags, reviewText: reviewText || undefined });
            setShowDislikePanel(false);
        },
        [submitFeedback]
    );

    const handleDislikeSkip = useCallback(() => {
        setShowDislikePanel(false);
        setCurrentRating(null);
    }, []);

    const isLiked = currentRating === 'like';
    const isDisliked = currentRating === 'dislike';
    const hasFeedback = messageId !== undefined;

    return (
        <Stack alignItems="flex-start" direction="column" spacing={0.5} sx={{ maxWidth: '78%' }}>
            <SaraCircleMark size={24} variant="sara" />
            <Typography
                component="div"
                sx={{
                    color: ASSISTANT_TEXT_COLOR,
                    fontSize: 16,
                    letterSpacing: '0.15px',
                    lineHeight: '24px',
                    whiteSpace: 'pre-wrap',
                    wordBreak: 'break-word',
                }}
            >
                {content}
            </Typography>

            {/* Single action bar — Figma: Retry → Like → Dislike → Copy, all right-aligned */}
            <Stack direction="row" justifyContent="flex-end" sx={{ width: '100%' }}>
                {onRetry && (
                    <Tooltip title="Retry">
                        <span>
                            <IconButton
                                aria-label="Retry response"
                                data-testid="assistant-message-retry"
                                disabled={isLoading}
                                onClick={onRetry}
                                size="small"
                                sx={{ color: 'grey.600' }}
                            >
                                <Refresh sx={{ fontSize: 16 }} />
                            </IconButton>
                        </span>
                    </Tooltip>
                )}
                {hasFeedback && (
                    <>
                        <Tooltip title={isLiked ? 'Good response' : 'Mark as helpful'}>
                            <IconButton
                                aria-label={isLiked ? 'Good response' : 'Mark response as helpful'}
                                aria-pressed={isLiked}
                                data-testid="assistant-feedback-like"
                                onClick={handleLike}
                                size="small"
                                sx={{ color: isLiked ? 'primary.main' : 'grey.600' }}
                            >
                                {isLiked ? (
                                    <ThumbUp sx={{ fontSize: 16 }} />
                                ) : (
                                    <ThumbUpOutlined sx={{ fontSize: 16 }} />
                                )}
                            </IconButton>
                        </Tooltip>
                        <Tooltip title={isDisliked ? 'Response reported' : 'Report a problem'}>
                            <IconButton
                                aria-label={
                                    isDisliked
                                        ? 'Response reported as problematic'
                                        : 'Report a problem with this response'
                                }
                                aria-pressed={isDisliked}
                                data-testid="assistant-feedback-dislike"
                                onClick={handleDislike}
                                size="small"
                                sx={{ color: isDisliked ? 'error.main' : 'grey.600' }}
                            >
                                {isDisliked ? (
                                    <ThumbDown sx={{ fontSize: 16 }} />
                                ) : (
                                    <ThumbDownOutlined sx={{ fontSize: 16 }} />
                                )}
                            </IconButton>
                        </Tooltip>
                    </>
                )}
                <Tooltip title={copied ? 'Copied!' : 'Copy to clipboard'}>
                    <IconButton
                        aria-label={copied ? 'Copied to clipboard' : 'Copy to clipboard'}
                        data-testid="assistant-message-copy"
                        onClick={handleCopy}
                        size="small"
                        sx={{ color: 'grey.600' }}
                    >
                        {copied ? <Check sx={{ fontSize: 16 }} /> : <ContentCopy sx={{ fontSize: 16 }} />}
                    </IconButton>
                </Tooltip>
            </Stack>

            {showDislikePanel && <DislikeFeedbackPanel onSkip={handleDislikeSkip} onSubmit={handleDislikeSubmit} />}
        </Stack>
    );
};

export default AssistantMessageBubble;
